import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { LuTarget } from 'react-icons/lu';

import { AccountGroupProfile, ConvictionType, ManagementType } from '@ndvr/portfolio-api';

import { BaseCard } from '@/components/BaseCard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAssetsByIdsMap } from '@/hooks/market-data-queries';
import { formatPercent } from '@/utils/number-formatter';
import { titleCase } from 'utils';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import {
    AccountGroupAllocations,
    getConvictions,
    getLifestyleProtectionInfo,
    useAccountGroupAllocations,
} from 'views/portfolio/PortfolioPage/tabs/target-allocation/allocationUtils';
import {
    formatConvictionValue,
    getConvictionTypeDisplay,
} from 'views/portfolio/PortfolioPage/tabs/target-allocation/convictionUtils';

import { ExtendedConviction } from '../target-allocation/types';

interface LifestyleProtectionSectionProps {
    lifestyleProtectionInfo?: { initialSecuredness: number };
}

const LifestyleProtectionSection = ({ lifestyleProtectionInfo }: LifestyleProtectionSectionProps) => {
    if (!lifestyleProtectionInfo) {
        return null;
    }
    return (
        <div className="space-y-3">
            <div className="text-sm font-medium text-gray-700">Lifestyle Protection</div>
            <div className="border-b border-gray-300 pb-3 text-sm text-gray-500">
                {formatPercent(lifestyleProtectionInfo.initialSecuredness * 100) + ' securedness'}
            </div>
        </div>
    );
};

interface ConvictionsSectionProps {
    convictions: Record<string, ExtendedConviction[]>;
}

const ConvictionsSection = ({ convictions }: ConvictionsSectionProps) => {
    if (isEmpty(convictions)) {
        return null;
    }

    const allConvictions = Object.values(convictions).flat();
    const convictionCount = allConvictions.length;
    const accountCount = Object.keys(convictions).length;

    return (
        <div className="space-y-3">
            <div className="text-sm font-medium text-gray-700">Convictions</div>
            <div className="text-xs text-gray-600">
                {convictionCount} securit{convictionCount !== 1 ? 'ies' : 'y'} across {accountCount} account
                {accountCount !== 1 ? 's' : ''}
            </div>
            <div className="flex flex-wrap gap-1 border-b border-gray-300 pb-4">
                {allConvictions.slice(0, 3).map((conviction: ExtendedConviction) => (
                    <TooltipProvider key={`${conviction.securityId}-${conviction.account}`}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="cursor-help rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700 transition-colors hover:bg-gray-200">
                                    {conviction.ticker || 'N/A'}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <div className="space-y-1">
                                    <div className="font-semibold">{conviction.ticker || 'N/A'}</div>
                                    <div className="text-xs text-gray-600">Account: {conviction.account}</div>
                                    <div className="text-xs">
                                        {getConvictionTypeDisplay(conviction.convictionType)}:{' '}
                                        {formatConvictionValue(conviction)}
                                        {conviction.convictionType === ConvictionType.WEIGHT && (
                                            <div className="text-xs text-gray-500 italic">(of Portfolio Total NAV)</div>
                                        )}
                                    </div>
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ))}
                {convictionCount > 3 && (
                    <div className="text-xs text-gray-500 italic">+{convictionCount - 3} more...</div>
                )}
            </div>
        </div>
    );
};

const GrowthAllocationSection = ({
    accountGroupProfile,
    allocations,
}: {
    accountGroupProfile?: AccountGroupProfile;
    allocations: AccountGroupAllocations;
}) => {
    return (
        <div className="space-y-3">
            <div className="text-sm font-medium text-gray-700">Growth Allocation</div>
            {accountGroupProfile?.accountGroup.allocationGUID ? (
                <div className="border-border truncate rounded-lg border bg-gray-50 p-3 text-xs text-gray-700">
                    {allocations.portfolioAllocation?.allocationName ?? 'No allocation'}
                </div>
            ) : (
                accountGroupProfile?.accounts.map((account) => (
                    <div key={account.accountGUID}>
                        <div className="mb-2 text-xs font-medium text-gray-600">{account.accountName.plainText}</div>
                        <div className="border-border truncate rounded-lg border bg-gray-50 p-3 text-xs text-gray-700">
                            {account.managementType !== ManagementType.AUTOMATIC
                                ? titleCase(account.managementType)
                                : (allocations.accountAllocations[account.accountGUID]?.allocationName ??
                                  'No allocation')}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

interface AllocationsCardProps {
    accountGroupGUID: string;
}

export const AllocationCard = ({ accountGroupGUID }: AllocationsCardProps) => {
    const { data: accountGroupProfile, isPending: isAccountGroupProfilePending } =
        useAccountGroupProfileWithAumIncluded(accountGroupGUID);
    const allocations = useAccountGroupAllocations(accountGroupProfile);

    const convictionSecurityIds = useMemo(() => {
        const ids = new Set<string>();
        accountGroupProfile?.accounts?.forEach((account) => {
            account.convictions?.forEach((conviction) => {
                ids.add(conviction.securityId);
            });
        });
        return Array.from(ids);
    }, [accountGroupProfile]);

    const { data: assetsMap } = useAssetsByIdsMap(convictionSecurityIds);

    const lifestyleProtectionInfo = getLifestyleProtectionInfo(accountGroupProfile);
    const convictions = getConvictions(accountGroupProfile, assetsMap);

    return (
        <BaseCard
            title="Target Allocation"
            icon={LuTarget}
            headerClassName="bg-[#C6E6E6]"
            loading={isAccountGroupProfilePending || allocations.isPending}
            navigateUrl="target-allocation"
        >
            <div className="space-y-3">
                <LifestyleProtectionSection lifestyleProtectionInfo={lifestyleProtectionInfo} />
                <ConvictionsSection convictions={convictions} />
                <GrowthAllocationSection accountGroupProfile={accountGroupProfile} allocations={allocations} />
            </div>
        </BaseCard>
    );
};
